import {
  Heading,
  Icon,
  IconButton,
  Logo,
  Menu,
  NavItem,
  WindowSizeConsumer
} from '@albert-io/atomic';
import {getCardClassName} from '@albert-io/atomic/utils/getCardClassName';
import classnames from 'classnames';
import sessionStore from 'client/Session/SessionStore';
import LogInLink from 'generic/LogInLink/LogInLink';
import SignUpLink from 'generic/SignUpLink/SignUpLink';
import type {Map as ImmutableMap} from 'immutable';
import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';
import {getUserQuery} from 'lib/UserAccessUtil';
import {camelCase} from 'lodash';
import type {KeyboardEvent, MouseEvent} from 'react';
import React, {useCallback, useEffect, useReducer, useRef, useState} from 'react';
import {Link} from 'react-router';
import type {UserModelV2} from 'resources/GeneratedModels/User/UserModel.v2';
import {HelpMenu} from '../NavWidgets/NavWidgets.react';

import './marketing-navigation.scss';
import {navLinks} from './navLinks';

/** One entry of the static `navLinks` tree; values are heterogeneous by design. */
type NavLink = ImmutableMap<string, any>;

/*
 * The four `@ts-expect-error`s below all have one upstream cause, newly visible now
 * that this file is type-checked: `NavItem`'s `Props<T extends ElementType = typeof
 * Link>` loses its type parameter through `forwardRef`, so every call site resolves
 * to `Props<typeof Link>` and `to` becomes required — unsatisfiable for any `as='a'`
 * / `'span'` / `'div'` override. `GlobalNavigation.tsx` carries the same suppression
 * for the same reason. Making the export generic is a shared-atomic change with
 * app-wide reach, so it is left as a follow-up rather than folded into this rewrite.
 */

/**
 * Returns the route for our "Subjects" menu item (based on session and account state).
 *
 * @see https://github.com/albert-io/project-management/issues/3301
 */
function generateSubjectsRoute(): string {
  return '/subjects';
}

function generateLinkProps(link: NavLink) {
  let route = link.get('route');
  if (link.get('name') === 'Library') {
    route = generateSubjectsRoute();
  }

  const LinkElement = link.get('hardLink', false) ? 'a' : Link;
  return {
    [link.get('hardLink', false) ? 'href' : 'to']: route,
    as: link.has('dropdown') ? 'div' : LinkElement
  };
}

interface NavState {
  isOpen: boolean;
  openDropdown: string | null;
  isMobile: boolean;
}

type NavAction =
  | {type: 'toggleMenu'}
  | {type: 'toggleDropdown'; name: string | undefined}
  | {type: 'viewportChange'; isMobile: boolean};

/**
 * The three keys transition together, so they share a reducer rather than three
 * `useState`s: `viewportChange` has to compare against the *current* `isMobile`
 * and clear the other two atomically, and it is dispatched from a microtask
 * where a render closure would already be stale.
 */
function navReducer(state: NavState, action: NavAction): NavState {
  switch (action.type) {
    case 'toggleMenu':
      return {...state, isOpen: !state.isOpen};
    case 'toggleDropdown':
      return {
        ...state,
        openDropdown: state.openDropdown === action.name ? null : (action.name ?? null)
      };
    case 'viewportChange':
      if (!action.isMobile && state.isMobile) {
        return {isOpen: false, openDropdown: null, isMobile: false};
      }
      if (action.isMobile !== state.isMobile) {
        return {...state, isMobile: action.isMobile};
      }
      return state;
    default:
      return state;
  }
}

interface Props {
  location?: any;
  user?: UserModelV2;
}

function MarketingNavigation({user}: Props) {
  const [{isOpen, openDropdown, isMobile}, dispatch] = useReducer(navReducer, {
    isOpen: false,
    openDropdown: null,
    isMobile: false
  });

  const toggleMobileMenu = useCallback(() => dispatch({type: 'toggleMenu'}), []);

  const toggleMobileDropdown = useCallback((e: MouseEvent<HTMLElement>) => {
    dispatch({type: 'toggleDropdown', name: e.currentTarget.dataset.dropdownName});
  }, []);

  const getNavLinks = (mobile: boolean) =>
    navLinks.map((link: NavLink) => {
      if (link.get('hideForNonMobile') && !mobile) {
        return null;
      }
      if (link.has('dropdown')) {
        return (
          <Dropdown
            key={camelCase(link.get('name'))}
            user={user}
            link={link}
            isMobile={mobile}
            toggleMobileDropdown={toggleMobileDropdown}
            openDropdown={openDropdown}
          />
        );
      }

      return (
        <li key={camelCase(link.get('name'))}>
          {/* @ts-expect-error `as` override — see the NavItem note above */}
          <NavItem onClick={toggleMobileMenu} {...generateLinkProps(link)}>
            {link.get('name')}
          </NavItem>
        </li>
      );
    });

  return (
    <WindowSizeConsumer>
      {({breakpoints, viewportWidth}) => {
        const nextIsMobile = viewportWidth <= breakpoints.m;
        if (nextIsMobile !== isMobile) {
          // Schedule state update after render to avoid setState-in-render
          Promise.resolve().then(() => dispatch({type: 'viewportChange', isMobile: nextIsMobile}));
        }
        return (
          <header
            className={classnames('marketing-header', {
              'marketing-header__mobile-menu--open': isOpen
            })}
          >
            {/* @ts-expect-error `as` override — see the NavItem note above */}
            <NavItem as='a' href='/' className='marketing-header__logo'>
              <Logo color='white' />
            </NavItem>
            {nextIsMobile && (
              <IconButton
                className='marketing-nav__menu-toggle'
                label={isOpen ? 'Close Menu' : 'Open Menu'}
                color='primary'
                variant={isOpen ? 'text' : 'solid'}
                icon={isOpen ? 'times' : 'bars'}
                onClick={toggleMobileMenu}
              />
            )}
            <nav
              className={classnames('marketing-nav', {'marketing-nav--open': isOpen})}
              aria-label='main'
            >
              <ul className='marketing-nav__menu'>
                {getNavLinks(nextIsMobile)}
                {!nextIsMobile && (
                  <li>
                    <HelpMenu wrapperClassName='marketing-help_menu' />
                  </li>
                )}
                <li className='marketing-nav__log-in'>
                  <LogInLink toggleMobileMenu={toggleMobileMenu} />
                </li>
                <li className='marketing-nav__sign-up'>
                  {/* @ts-expect-error `as` override — see the NavItem note above */}
                  <NavItem as='span' stateless>
                    <SignUpLink toggleMobileMenu={toggleMobileMenu} />
                  </NavItem>
                </li>
              </ul>
            </nav>
          </header>
        );
      }}
    </WindowSizeConsumer>
  );
}

export default awaitMandarkQueries(() => {
  // `passthroughProps` is required by the HOC's signature but read as
  // `passthroughProps || {}`, so an empty object matches the previous `undefined`.
  if (!sessionStore.hasValidSession()) {
    return {
      queries: [],
      passthroughProps: {}
    };
  }
  return {
    queries: {
      user: getUserQuery()
    },
    passthroughProps: {}
  };
}, MarketingNavigation);

interface DropdownProps {
  link: NavLink;
  isMobile?: boolean;
  toggleMobileDropdown?: (e: MouseEvent<HTMLElement>) => void;
  openDropdown?: string | null;
  /** Unused in render; kept so the parent's call site stays type-checked. */
  user?: UserModelV2;
}

function getCardItem(content: NavLink) {
  const route: string = content.get('route');
  const body = (
    <>
      {content.get('image') && (
        <img
          className='marketing-nav__card-image'
          src={content.get('image')}
          alt=''
          loading='lazy'
        />
      )}
      <div className='marketing-nav__card-details'>
        <div className='marketing-nav__card-title'>{content.get('name')}</div>
        {content.get('description') && (
          <div className='marketing-nav__card-description'>{content.get('description')}</div>
        )}
      </div>
      {content.get('cta') && (
        <span className='marketing-nav__card-cta'>
          {content.get('cta')}&nbsp;
          <Icon icon='arrow-right' iconStyle='light' />
        </span>
      )}
    </>
  );

  return content.get('hardLink', false) ? (
    <a className='marketing-nav__card-item' href={route}>
      {body}
    </a>
  ) : (
    <Link className='marketing-nav__card-item' to={route}>
      {body}
    </Link>
  );
}

function getColumnItem(link: NavLink) {
  const route: string = link.get('route');
  const body = (
    <div className='marketing-nav__column-item-details'>
      <div className='marketing-nav__column-item-title' style={{color: link.get('color')}}>
        {link.get('name')}
      </div>
      <div className='marketing-nav__column-item-description'>{link.get('description')}</div>
    </div>
  );

  return link.get('hardLink', false) ? (
    <a className='marketing-nav__column-item' href={route}>
      {body}
    </a>
  ) : (
    <Link className='marketing-nav__column-item' to={route}>
      {body}
    </Link>
  );
}

function getColumnDropdown(link: NavLink) {
  const dropdownItems = link.get('dropdown');
  const leftContent = dropdownItems.get('leftContent');
  const rightContent = dropdownItems.get('rightContent');

  // Card-style layout: each column is a single card (e.g. Pricing)
  if (!leftContent.has('links')) {
    return (
      <div className='marketing-nav__card-dropdown'>
        {getCardItem(leftContent)}
        {getCardItem(rightContent)}
      </div>
    );
  }

  // Legacy column layout with lists of links
  return (
    <ul className='marketing-nav__column-dropdown'>
      <li className='marketing-nav__column'>
        <Heading size='2xs' color='primary' className='marketing-nav__column-title'>
          {leftContent.get('name')}
        </Heading>
        <ul className='marketing-nav__column-list'>
          {leftContent.get('links').map((columnLink: NavLink) => {
            return <li key={camelCase(columnLink.get('name'))}>{getColumnItem(columnLink)}</li>;
          })}
        </ul>
      </li>
      <li className='marketing-nav__column'>
        <Heading size='2xs' color='primary' className='marketing-nav__column-title'>
          {rightContent.get('name')}
        </Heading>
        <ul className='marketing-nav__column-list'>
          {rightContent.get('links').map((columnLink: NavLink) => {
            return <li key={camelCase(columnLink.get('name'))}>{getColumnItem(columnLink)}</li>;
          })}
        </ul>
      </li>
    </ul>
  );
}

function getDropdown(link: NavLink) {
  return link.get('dropdown').map((item: NavLink) => {
    const isHardLink = item.get('hardLink', false);
    const LinkComponent = isHardLink ? 'a' : Link;
    const linkProps = isHardLink ? {href: item.get('route')} : {to: item.get('route')};

    return (
      <Menu.Item
        key={camelCase(item.get('name'))}
        as={LinkComponent}
        preventDefaultClickHandler={false}
        {...linkProps}
      >
        {item.get('name')}
      </Menu.Item>
    );
  });
}

function getNextItemInList(
  itemsList: ArrayLike<Element>,
  currentItem: EventTarget | null,
  nextIndex: number
) {
  let itemIndex = Array.prototype.indexOf.call(itemsList, currentItem) + nextIndex;
  if (nextIndex < 0) {
    itemIndex = itemIndex < 0 ? itemsList.length - 1 : itemIndex;
  } else {
    itemIndex = itemIndex === itemsList.length ? 0 : itemIndex;
  }
  return itemsList[itemIndex] as HTMLElement;
}

function Dropdown({link, isMobile, toggleMobileDropdown, openDropdown}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);

  const containerRef = useRef<HTMLLIElement>(null);
  const navItemRef = useRef<HTMLElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleToggle = useCallback(() => setIsOpen((prev) => !prev), []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    navItemRef.current?.focus();
  }, []);

  // The class read `this.state.isOpen` live inside a listener it registered once
  // for the component's lifetime. Mirroring the value keeps that shape: a plain
  // render closure would go stale, and adding `isOpen` to the deps below would
  // re-subscribe on every toggle instead of holding one listener.
  const isOpenRef = useRef(isOpen);
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    const onClickOutsideComponent = (e: globalThis.MouseEvent) => {
      if (isOpenRef.current && !containerRef.current?.contains(e.target as Node)) {
        handleClose();
      }
    };
    global.addEventListener('click', onClickOutsideComponent);
    return () => global.removeEventListener('click', onClickOutsideComponent);
  }, [handleClose]);

  const handleNavItemKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    // Enter Key
    if (e.key === 'Enter') {
      handleToggle();
    }
    // ESC Key
    if (e.key === 'Escape') {
      handleClose();
    }
    // Right/Down Arrow Keys
    if (['ArrowRight', 'ArrowDown'].includes(e.key) && isOpen) {
      e.preventDefault();
      dropdownRef.current
        ?.querySelector<HTMLElement>(
          '[role="menuitem"], .marketing-nav__dropdown-item a, .marketing-nav__column-item, .marketing-nav__card-item'
        )
        ?.focus();
    }
  };

  const handleDropdownKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    const focusableSelector =
      '[role="menuitem"], .marketing-nav__dropdown-item a, .marketing-nav__card-item';
    const items = Array.from(
      dropdownRef.current?.querySelectorAll<HTMLElement>(focusableSelector) ?? []
    );

    // ESC & Tab Key
    if (e.key === 'Escape' || (e.key === 'Tab' && e.target === items[items.length - 1])) {
      handleClose();
    }
    // Right/Down Arrow Keys
    if (['ArrowRight', 'ArrowDown'].includes(e.key)) {
      e.preventDefault();
      const index = items.indexOf(e.target as HTMLElement);
      items[(index + 1) % items.length].focus();
    }
    // Left/Up Arrow Keys
    if (['ArrowLeft', 'ArrowUp'].includes(e.key)) {
      e.preventDefault();
      const index = items.indexOf(e.target as HTMLElement);
      items[(index - 1 + items.length) % items.length].focus();
    }
  };

  const handleMegaDropdownKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    // ESC & Tab Key
    if (
      e.key === 'Escape' ||
      (e.key === 'Tab' &&
        dropdownRef.current?.firstChild?.lastChild?.lastChild?.lastChild === target.parentNode)
    ) {
      handleClose();
    }
    // Down Arrow Key
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const columnItems = dropdownRef.current!.querySelectorAll('.marketing-nav__column-item');
      getNextItemInList(columnItems, target, 1).focus();
    }
    // Up Arrow Key
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const columnItems = dropdownRef.current!.querySelectorAll('.marketing-nav__column-item');
      getNextItemInList(columnItems, target, -1).focus();
    }
    // Left/Right Arrow Keys
    if (['ArrowRight', 'ArrowLeft'].includes(e.key)) {
      e.preventDefault();
      const {nextSibling, previousSibling} = target.parentNode!.parentNode!.parentNode as Element;
      if (nextSibling) {
        (nextSibling as Element).querySelector<HTMLElement>('.marketing-nav__column-item')?.focus();
      } else {
        (previousSibling as Element)
          .querySelector<HTMLElement>('.marketing-nav__column-item')
          ?.focus();
      }
    }
  };

  const dropdownItems = link.get('dropdown');
  const dropdownCard = dropdownItems.has('leftContent')
    ? getColumnDropdown(link)
    : getDropdown(link);

  const isColumnLayout =
    dropdownItems.has('leftContent') && dropdownItems.getIn(['leftContent', 'links']);
  const dropdownClasses = classnames('marketing-nav__dropdown', {
    'marketing-nav__dropdown--with-columns': isColumnLayout
  });

  return (
    <li
      key={camelCase(link.get('name'))}
      ref={containerRef}
      className={classnames({
        'marketing-nav__has-dropdown': link.has('dropdown'),
        'marketing-nav__has-dropdown--open': openDropdown === link.get('name')
      })}
    >
      {/* @ts-expect-error `as` override — see the NavItem note above */}
      <NavItem
        {...generateLinkProps(link)}
        ref={navItemRef}
        tabIndex={0}
        icon='caret-down'
        iconPosition='right'
        aria-haspopup
        aria-expanded={isOpen || openDropdown === link.get('name')}
        data-dropdown-name={isMobile ? link.get('name') : null}
        onClick={isMobile ? toggleMobileDropdown : undefined}
        onKeyDown={handleNavItemKeyDown}
      >
        {link.get('name')}
      </NavItem>
      <div
        ref={dropdownRef}
        hidden={!isOpen}
        className={classnames(dropdownClasses, getCardClassName({shadow: 'default', border: 'none'}))}
        onKeyDown={isColumnLayout ? handleMegaDropdownKeyDown : handleDropdownKeyDown}
      >
        {dropdownCard}
      </div>
    </li>
  );
}
